import { useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { guardedLeave, MenuSurface, MenuSurfaceItem, Pill, SideSheet, useToast } from "../ui";
import { useNextT } from "../i18n";
import { useCurrentUser, useIsSuperAdmin } from "../bridges/session";
import { useAccess as useNextAccess } from "../access/api";
import { LegacyExportDataModal, LegacyLegalDocumentsDialog, useAccess, useBillingState, useSignOut } from "../bridges/frame";
import { CONTACT_PATH } from "../contactUs/paths";
import { useMyAccounts, useSwitchAccount } from "../team/api";
import { closedContact, errorText } from "../team/errors";
import { Icon } from "./icons";
import { roleWordKey } from "./roleLabel";
import { FRAME_RELEASED, setPersonalFrame } from "./frameMode";
import { switchLegacyView } from "../preview/legacyView";
import { hasPublicPreviewCookie, turnOffPublicPreview } from "../public/mode";
import { isPreviewUser } from "../preview/previewFlag";
import s from "./AccountMenu.module.scss";

const initials = (u: any) => `${(u?.first_name || "?")[0]}${(u?.last_name || "")[0] || ""}`.toUpperCase();

/** Unicode isolates around a name inside a plain-string message: the same job as <bdi>. */
const isolate = (text: string) => `\u2068${text}\u2069`;

/**
 * The switcher's list (7A, board 94): the accounts as the server states them. A closed account says "Closed" and, on click,
 * why (its Owner to contact); a view-only one says "View-only". Mounted only while the sheet is open, so the accounts are
 * asked for when someone looks. Until they arrive, today's list of the person's workspaces stands in, all open.
 * Switching is `me/switch`. Staff viewing as someone switch too (owner note A4): the server moves the viewing-as session to one
 * of THAT person's accounts (a 404 for any other), keeps the top strip, and records it; nothing is blocked or toasted here.
 */
const AccountSwitcher = ({ fallback, currentId, run, onDone }: { fallback: { id: number; name: string }[]; currentId: number | null | undefined; run: (go: () => void) => void; onDone: () => void }) => {
  const { t } = useNextT();
  const mine = useMyAccounts();
  const switchAccount = useSwitchAccount();
  const toast = useToast();
  const accounts: { id: number; name: string; state: "ok" | "view_only" | "closed"; current: boolean }[] =
    mine.data ?? fallback.map((w) => ({ id: w.id, name: w.name, state: "ok" as const, current: w.id === currentId }));
  const attempt = (a: { id: number; name: string }) => {
    switchAccount.mutateAsync(a.id)
      .then(() => { onDone(); window.location.href = "/"; })
      .catch((err) => {
        const closed = closedContact(err);
        // The reason is a toast (the sheet may already be gone on a phone, where the drawer closes first).
        if (closed) toast({ message: closed.name ? t("shell.account.closed_reason", { account: isolate(a.name), name: isolate(closed.name) }) : t("shell.account.closed_reason_unnamed", { account: isolate(a.name) }), tone: "danger" });
        else toast({ message: errorText(err, t), tone: "danger" });
      });
  };
  return (
    <>
      <ul className={s.wsList}>
        {accounts.map((a) => (
          <li key={a.id}>
            {/* `run` is the menu's way out: the phone drawer closes first, then a page with unsaved changes asks. */}
            <button type="button" className={s.wsChoice} aria-current={a.current ? "true" : undefined} disabled={switchAccount.isPending} onClick={() => run(() => attempt(a))}>
              <bdi>{a.name}</bdi>
              {a.state === "closed" && <Pill tone="neutral">{t("shell.account.closed")}</Pill>}
              {a.state === "view_only" && <Pill tone="warning">{t("shell.account.view_only")}</Pill>}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
};

/** The account card at the bottom of the menu column: avatar, name, "workspace · role"; opens the account menu upward (a bottom sheet on phones).
 *  onChoose (phone drawer): Settings and Sign out wait for the drawer to close first, like the menu items. */
export const AccountMenu = ({ onChoose }: { onChoose?(go: () => void): void }) => {
  const { t } = useNextT();
  const navigate = useNavigate();
  const user = useCurrentUser() as any;
  const isSuper = useIsSuperAdmin();
  const { isVolunteerOnly } = useAccess();
  const role = useNextAccess().data?.role;
  // The role as words comes from the access answer; until it arrives nothing is guessed.
  const roleWord = role ? t(roleWordKey(role)) : null;
  const signOut = useSignOut();
  const toast = useToast();
  const nameId = useId();
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [picker, setPicker] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const { locked } = useBillingState();
  if (!user) return null;
  const viewingAs = Boolean(user.is_impersonating); // the server flag from /me
  // A locked account: its Owner keeps Plans and billing, Download your data and Contact us; everyone else only Switch account and Sign out.
  const lockedOwner = locked && role === "owner";
  const ws = user.workspaces?.find((w: any) => w.id === user.currentWorkspace);
  const many = (user.workspaces?.length ?? 0) > 1;
  const close = () => setOpen(false);
  const name = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  // Leaving the page: a page with unsaved changes asks first (`guardedLeave`), after the drawer has closed.
  const act = (go: () => void) => { close(); const leave = () => guardedLeave(go); if (onChoose) onChoose(leave); else leave(); };

  const header = (
    <>
      <div className={s.who}>
        <span aria-hidden="true" className={s.avatarSmall}>{initials(user)}</span>
        <span className={s.text}>
          <strong id={nameId}><bdi>{name}</bdi></strong>
          <span className={s.muted}><bdi>{user.email}</bdi></span>
        </span>
      </div>
      {ws && (
        <div className={s.ws}>
          <div className={s.label}>{t("shell.account.workspace")}</div>
          <div className={s.wsName}><bdi>{ws.name}</bdi></div>
          {roleWord && <div className={s.muted}>{roleWord}</div>}
        </div>
      )}
    </>
  );

  return (
    <>
      <button ref={btn} type="button" className={s.card} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(true)}>
        <span aria-hidden="true" className={s.avatarSmall}>{initials(user)}</span>
        <span className={s.text}>
          <span className={s.cardName}><bdi>{name}</bdi></span>
          {ws && <span className={s.cardSub}><bdi>{ws.name}</bdi>{roleWord && <> · {roleWord}</>}</span>}
        </span>
        <span aria-hidden="true" className={s.chevron}><Icon name="updown" size={18} /></span>
      </button>
      <MenuSurface anchorEl={btn.current} open={open} onClose={close} label={t("shell.account.label")} header={header} labelledBy={nameId} placement="above-start">
        {lockedOwner && <MenuSurfaceItem icon={<Icon name="billing" />} onClick={() => act(() => navigate("/billing"))}>{t("contactUs.locked.plans")}</MenuSurfaceItem>}
        {lockedOwner && <MenuSurfaceItem icon={<Icon name="export" />} onClick={() => act(() => setExportOpen(true))}>{t("shell.nav.export")}</MenuSurfaceItem>}
        {lockedOwner && <MenuSurfaceItem icon={<Icon name="help" />} onClick={() => act(() => navigate(CONTACT_PATH))}>{t("shell.top.contact_us")}</MenuSurfaceItem>}
        {many && <MenuSurfaceItem icon={<Icon name="swap" />} onClick={() => { close(); setPicker(true); }}>{t("shell.account.switch")}</MenuSurfaceItem>}
        {/* My settings stays for the Owner of a locked account (walkthrough decision 7: password, sign out everywhere, leave). */}
        {(!locked || lockedOwner) && !isVolunteerOnly && <MenuSurfaceItem icon={<Icon name="settings" />} onClick={() => act(() => navigate("/settings"))}>{t("shell.account.settings")}</MenuSurfaceItem>}
        {isSuper && !FRAME_RELEASED && <MenuSurfaceItem checked icon={<Icon name="eye" />} onClick={() => { setPersonalFrame(false); window.location.reload(); }}>{t("shell.account.new_frame_on")}</MenuSurfaceItem>}
        {/* "Show me the old version" (staff only, saved on the server): today's pages and frame for this person, for comparison. Back from the old menu. */}
        {isSuper && !viewingAs && <MenuSurfaceItem icon={<Icon name="swap" />} onClick={() => act(() => { switchLegacyView(true).catch((err) => toast({ message: errorText(err, t), tone: "danger" })); })}>{t("shell.account.old_version_on")}</MenuSurfaceItem>}
        {/* The secret-link visitor's way out (a person the staff listed cannot turn it off here: the server decides for them). */}
        {hasPublicPreviewCookie() && !isPreviewUser() && <MenuSurfaceItem icon={<Icon name="swap" />} onClick={() => act(turnOffPublicPreview)}>{t("shell.account.preview_off")}</MenuSurfaceItem>}
        {/* Terms & Privacy lives here in the new frame; today's floating pill covered this card (walkthrough 2026-10-29, issue 3). */}
        <MenuSurfaceItem icon={<Icon name="reports" />} onClick={() => { close(); const go = () => setTermsOpen(true); if (onChoose) onChoose(go); else go(); }}>{t("shell.account.terms")}</MenuSurfaceItem>
        <MenuSurfaceItem icon={<Icon name="signOut" />} onClick={() => act(signOut)}>{t("shell.account.sign_out")}</MenuSurfaceItem>
      </MenuSurface>
      <LegacyLegalDocumentsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
      {exportOpen && <LegacyExportDataModal open={exportOpen} onClose={() => setExportOpen(false)} />}
      <SideSheet open={picker} title={t("shell.account.switch_title")} onClose={() => setPicker(false)}>
        {picker && <AccountSwitcher fallback={user.workspaces ?? []} currentId={user.currentWorkspace} run={act} onDone={() => setPicker(false)} />}
      </SideSheet>
    </>
  );
};
